"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonLoading,
  IonToast,
  useIonViewWillEnter,
} from "@ionic/react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  IconButton,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Container,
  Stack,
  Autocomplete,
  Divider,
  Avatar,
} from "@mui/material"
import {
  Add,
  Delete,
  ExpandMore,
  Inventory,
  ShoppingCart,
  Cancel,
  CheckCircle,
  Pending,
  Warning,
  Refresh,
  Receipt,
  Event,
  Person,
  Phone,
  Badge,
  Storefront,
  People,
  Group,
  Loyalty,
  Email,
  Business,
  AttachMoney,
  ShoppingBag,
} from "@mui/icons-material"
import { styled } from "@mui/material/styles"
 
import { getCustomerStatusReport, getCustomerOrderDetails } from "../../../api/reports/reports"
import { getTlocations_production_centre } from "../../../api/stock-transfer/stock-transfer"
import { getAllCustomers2, getCustomers } from "../../../api/customers/customers"

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  border: "1px solid rgba(0,0,0,0.06)",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
  },
}))

const HeaderCard = styled(Box)(({ theme }) => ({
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  padding: theme.spacing(3),
  borderRadius: "12px 12px 0 0",
}))

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  textAlign: "center",
  borderRadius: 8,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
}))

interface Customer {
  user_id: string
  firstName: string
  lastName: string
  phone: string
  email: string
  altEmail: string
  business_id: string
  roleID: number
  total_amount: string
  total_orders: number
}

interface CustomerOrder {
  id: string
  order_id: string
  customer_id: string
  customer_name: string
  customer_phone: string
  customer_email: string
  branch_id: string
  created_at: string
  total_amount: string
  status: string
  items_count: number
  last_order_date: string
}

const CustomerStatusReportPage: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([])
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]) // Store all customers for stats
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null)
  const [startDate, setStartDate] = useState<string | null>(null)
  const [endDate, setEndDate] = useState<string | null>(null)
  const [customerOrders, setCustomerOrders] = useState<CustomerOrder[] | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [selectedOrderDetails, setSelectedOrderDetails] = useState<any | null>(null)
  const [branches, setBranches] = useState<any[]>([])
  const [statusFilter, setStatusFilter] = useState<string>("all")

  useEffect(() => {
    fetchBranches()
    fetchAllCustomers()
  }, [])

  useIonViewWillEnter(() => {
    fetchBranches()
    fetchAllCustomers()
  })

  const fetchBranches = async () => {
    try {
      const response = await getTlocations_production_centre()
      console.log(response)
      setBranches(response.data.data || [])
    } catch (error) {
      console.log(error)
      setToastMessage("Failed to fetch branches")
      setShowToast(true)
    }
  }

  const fetchAllCustomers = async () => {
    setLoading(true)
    try {
      const response = await getAllCustomers2()
      console.log("Customers response:", response)
      // Assuming the API returns an array of customers in response.data
      const customerData = response.data || response || []
      
      // Store all customers for statistics
      setAllCustomers(customerData)
      
      // Filter out customers with total_amount <= 0
      const filteredCustomers = customerData.filter(
        (customer: Customer) => {
          const totalAmount = parseFloat(customer.total_amount || '0');
          return totalAmount > 0;
        }
      )
      
      setCustomers(filteredCustomers)
      
      if (filteredCustomers.length === 0) {
        setToastMessage("No customers with spending found.")
        setShowToast(true)
      } else if (filteredCustomers.length < customerData.length) {
        const filteredOutCount = customerData.length - filteredCustomers.length;
        setToastMessage(`Showing ${filteredCustomers.length} of ${customerData.length} customers (${filteredOutCount} customers with zero or no spending filtered out).`)
        setShowToast(true)
      }

    } catch (error) {
      console.log(error)
      setToastMessage("Failed to fetch customers")
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const fetchCustomerOrders = async (customerId: string) => {
    if (!startDate || !endDate) {
      setToastMessage("Please select a date range first.")
      setShowToast(true)
      return
    }
    
    setLoading(true)
    setCustomerOrders(null)
    try {
      const response = await getCustomerStatusReport({
        customer_id: customerId,
        startDate,
        endDate,
        branch_id: "all",
        status: statusFilter !== "all" ? statusFilter : undefined,
      })
      console.log(response);

      setCustomerOrders(response.data)
      if (response.data.length === 0) {
        setToastMessage("No orders found for this customer in the selected period.")
        setShowToast(true)
      }
    } catch (error) {
      console.error("Error fetching customer orders:", error)
      setToastMessage("Failed to fetch customer orders.")
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleViewCustomerOrders = (customer: Customer) => {
    setSelectedCustomer(customer.user_id)
    fetchCustomerOrders(customer.user_id)
  }

  const handleViewOrderDetails = async (order_id: string) => {
    setDetailsOpen(true)
    setDetailsLoading(true)
    try {
      const response = await getCustomerOrderDetails({
        order_id,
        customer_id: selectedCustomer!,
      })
      console.log(response);
      setSelectedOrderDetails(response)
    } catch (error) {
      console.error("Error fetching order details:", error)
      setToastMessage("Failed to fetch order details.")
      setShowToast(true)
    } finally {
      setDetailsLoading(false)
    }
  }

  // Calculate totals for the summary cards
  const calculateTotals = () => {
    const totalAllCustomers = allCustomers?.length || 0;
    const totalCustomers = customers?.length || 0;
    const customersWithPhone = customers?.filter(customer => customer.phone).length || 0;
    const customersWithEmail = customers?.filter(customer => customer.email).length || 0;
    const totalRevenue = customers?.reduce((acc, customer) => acc + parseFloat(customer.total_amount || '0'), 0) || 0;
    const totalOrders = customers?.reduce((acc, customer) => acc + (customer.total_orders || 0), 0) || 0;
    
    return { 
      totalAllCustomers,
      totalCustomers, 
      customersWithPhone, 
      customersWithEmail, 
      totalRevenue, 
      totalOrders 
    };
  };

  const totals = calculateTotals();

  const getCustomerDisplayName = (customer: Customer) => {
    if (customer.firstName && customer.lastName) {
      return `${customer.firstName} ${customer.lastName}`;
    }
    return customer.firstName || customer.email || "Unknown Customer";
  };

  const getInitials = (customer: Customer) => {
    const name = getCustomerDisplayName(customer);
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "COMPLETED": return "success";
      case "PENDING": return "warning";
      case "CANCELLED": return "error";
      case "PROCESSING": return "info";
      default: return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "COMPLETED": return <CheckCircle />;
      case "PENDING": return <Pending />;
      case "CANCELLED": return <Cancel />;
      case "PROCESSING": return <Refresh />;
      default: return <Warning />;
    }
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `₦${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <IonPage className="pushDown">
      <IonHeader className="ion-no-border ">
        <IonToolbar>
          <IonTitle>Customer Status Report</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <Container maxWidth="lg" sx={{ py: 2 }}>
          {/* Customer Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <StatsCard>
                <Typography variant="h6">Active Customers</Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  {totals.totalCustomers}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  of {totals.totalAllCustomers} total
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} md={3}>
              <StatsCard>
                <Typography variant="h6">Customers with Phone</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {totals.customersWithPhone}
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} md={3}>
              <StatsCard>
                <Typography variant="h6">Total Orders</Typography>
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  {totals.totalOrders}
                </Typography>
              </StatsCard>
            </Grid>
            <Grid item xs={12} md={3}>
              <StatsCard>
                <Typography variant="h6">Total Revenue</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {formatCurrency(totals.totalRevenue)}
                </Typography>
              </StatsCard>
            </Grid>
          </Grid>

          {/* Customers List Table */}
          <StyledCard>
            <HeaderCard>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="600" color="#1e293b">
                  Active Customers (With Spending)
                </Typography>
                <Box>
                  <Chip 
                    label={`${customers.length} active customers`}
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                  {totals.totalAllCustomers > totals.totalCustomers && (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      ({totals.totalAllCustomers - totals.totalCustomers} zero-spending customers filtered)
                    </Typography>
                  )}
                </Box>
              </Box>
            </HeaderCard>
            <CardContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Contact Information</TableCell>
                      <TableCell align="right">Total Orders</TableCell>
                      <TableCell align="right">Total Spent</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow key={customer.user_id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {getInitials(customer)}
                            </Avatar>
                            <Box>
                              <Typography fontWeight="500">
                                {getCustomerDisplayName(customer)}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            {customer.phone && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Phone fontSize="small" sx={{ color: "#64748b" }} />
                                <Typography variant="body2">{customer.phone}</Typography>
                              </Box>
                            )}
                            {customer.email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Email fontSize="small" sx={{ color: "#64748b" }} />
                                <Typography variant="body2">{customer.email}</Typography>
                              </Box>
                            )}
                            {customer.altEmail && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Email fontSize="small" sx={{ color: "#64748b" }} />
                                <Typography variant="body2">{customer.altEmail}</Typography>
                              </Box>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                            <ShoppingBag fontSize="small" sx={{ color: "#64748b" }} />
                            <Typography variant="h6" fontWeight="600" color="primary.main">
                              {customer.total_orders || 0}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                            <AttachMoney fontSize="small" sx={{ color: "#64748b" }} />
                            <Typography variant="h6" fontWeight="600" color="success.main">
                              {formatCurrency(customer.total_amount || '0')}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </StyledCard>

          {/* Customer Orders Section */}
          {customerOrders && selectedCustomer && (
            <StyledCard sx={{ mt: 3 }}>
              <HeaderCard>
                <Typography variant="h6" fontWeight="600" color="#1e293b">
                  Orders for {getCustomerDisplayName(customers.find(c => c.user_id === selectedCustomer)!)}
                </Typography>
              </HeaderCard>
              <CardContent>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} md={3}>
                    <StatsCard>
                      <Typography variant="h6">Total Orders</Typography>
                      <Typography variant="h4" fontWeight="bold" color="primary.main">
                        {customerOrders.length}
                      </Typography>
                    </StatsCard>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StatsCard>
                      <Typography variant="h6">Total Revenue</Typography>
                      <Typography variant="h4" fontWeight="bold" color="success.main">
                        {formatCurrency(customerOrders.reduce((acc, order) => acc + parseFloat(order.total_amount), 0))}
                      </Typography>
                    </StatsCard>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StatsCard>
                      <Typography variant="h6">Completed</Typography>
                      <Typography variant="h4" fontWeight="bold" color="success.main">
                        {customerOrders.filter(order => order.status === "COMPLETED").length}
                      </Typography>
                    </StatsCard>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StatsCard>
                      <Typography variant="h6">Pending</Typography>
                      <Typography variant="h4" fontWeight="bold" color="warning.main">
                        {customerOrders.filter(order => order.status === "PENDING").length}
                      </Typography>
                    </StatsCard>
                  </Grid>
                </Grid>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order ID</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell align="right">Items</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {customerOrders.map((order) => (
                        <TableRow key={order.id}>
                          <TableCell>{order.order_id}</TableCell>
                          <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            {branches.find(b => b.branch_id === order.branch_id)?.name || order.branch_id}
                          </TableCell>
                          <TableCell align="right">{order.items_count}</TableCell>
                          <TableCell align="right">{formatCurrency(order.total_amount)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={order.status} 
                              color={
                                order.status === "COMPLETED" ? "success" :
                                order.status === "PENDING" ? "warning" :
                                order.status === "CANCELLED" ? "error" : "default"
                              } 
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleViewOrderDetails(order.order_id)}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </StyledCard>
          )}
        </Container>

        {/* Order Details Dialog */}
        <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, borderBottom: "1px solid #e2e8f0" }}>
            <Receipt sx={{ color: "#64748b" }} />
            Order Details
          </DialogTitle>
          <DialogContent sx={{ bgcolor: "#f8fafc", p: 3 }}>
            {detailsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
                <CircularProgress />
              </Box>
            ) : selectedOrderDetails ? (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#1e293b" }}>
                    Order Information
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Event fontSize="small" sx={{ color: "#64748b" }} />
                      <Typography variant="body2" color="text.secondary">
                        Order Date:{" "}
                        <Typography component="span" fontWeight="500" color="text.primary">
                          {new Date(selectedOrderDetails.created_at).toLocaleString()}
                        </Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Storefront fontSize="small" sx={{ color: "#64748b" }} />
                      <Typography variant="body2" color="text.secondary">
                        Branch:{" "}
                        <Typography component="span" fontWeight="500" color="text.primary">
                          {branches.find(b => b.branch_id === selectedOrderDetails.branch_id)?.name || selectedOrderDetails.branch_id}
                        </Typography>
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={selectedOrderDetails.status}
                        color={
                          selectedOrderDetails.status === "COMPLETED" ? "success" :
                          selectedOrderDetails.status === "PENDING" ? "warning" :
                          selectedOrderDetails.status === "CANCELLED" ? "error" : "default"
                        }
                        size="small"
                      />
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "#1e293b" }}>
                    Order Summary
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="h6" fontWeight="600" color="primary.main" sx={{ textAlign: 'center' }}>
                      Total: {formatCurrency(selectedOrderDetails.total_amount)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            ) : (
              <Typography sx={{ p: 4, textAlign: "center" }}>No details found.</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: "1px solid #e2e8f0" }}>
            <Button onClick={() => setDetailsOpen(false)} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <IonLoading isOpen={loading} message="Processing..." />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          position="top"
        />
      </IonContent>
    </IonPage>
  )
}

export default CustomerStatusReportPage